import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import {
  getToken,
  setToken as persistToken,
  clearToken,
  setUnauthorizedHandler,
  login as apiLogin,
  signup as apiSignup,
  getMe,
  type AuthUser,
  type MeResponse,
} from '../lib/api'

// AuthContext holds the session (JWT + who you are) for the whole SPA. The token
// is persisted in localStorage by lib/api so a refresh keeps you signed in; on a
// 401 (expired 12h token) we clear it and the router bounces you to /login.
interface AuthState {
  token: string | null
  user: AuthUser | null
  me: MeResponse | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  signup: (orgName: string, email: string, password: string) => Promise<void>
  logout: () => void
  refreshMe: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getToken())
  const [user, setUser] = useState<AuthUser | null>(null)
  const [me, setMe] = useState<MeResponse | null>(null)
  // loading is true until we've resolved whether an existing token is still valid.
  const [loading, setLoading] = useState<boolean>(!!getToken())

  const logout = useCallback(() => {
    clearToken()
    setToken(null)
    setUser(null)
    setMe(null)
  }, [])

  // Wire the api layer's 401 handler once: any authed call that 401s logs us out.
  useEffect(() => {
    setUnauthorizedHandler(logout)
  }, [logout])

  const refreshMe = useCallback(async () => {
    if (!getToken()) return
    try {
      const m = await getMe()
      setMe(m)
      setUser({ id: m.user_id, email: m.email, org_id: m.org_id, role: m.role })
    } catch {
      // handler already logged us out on 401; nothing else to do.
    }
  }, [])

  // On mount (or when a token appears), validate it by loading /me.
  useEffect(() => {
    if (!token) {
      setLoading(false)
      return
    }
    setLoading(true)
    refreshMe().finally(() => setLoading(false))
  }, [token, refreshMe])

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiLogin(email, password)
    persistToken(res.token)
    setToken(res.token)
    setUser(res.user)
  }, [])

  const signup = useCallback(async (orgName: string, email: string, password: string) => {
    const res = await apiSignup(orgName, email, password)
    persistToken(res.token)
    setToken(res.token)
    setUser(res.user)
  }, [])

  return (
    <AuthContext.Provider value={{ token, user, me, loading, login, signup, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
